/**
 * numbers.ts — the single arithmetic chokepoint.
 *
 * All cost/bulk/prestige/format math is routed through here so that the
 * backing numeric representation could be swapped (e.g. to a big-number lib)
 * in exactly one place without touching game systems.
 */

/** Suffix table for compact formatting. Beyond the table we fall back to scientific. */
const SUFFIXES = [
  '', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc',
  'UDc', 'DDc', 'TDc', 'QaDc', 'QiDc',
];

/**
 * Guard against NaN / Infinity poisoning state. Every value crossing a system
 * boundary should pass through here.
 */
export function safeNumber(n: number, fallback = 0): number {
  if (typeof n !== 'number' || Number.isNaN(n) || !Number.isFinite(n)) return fallback;
  return n;
}

/** Clamp a value into [min, max]. */
export function clamp(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/**
 * Format a number for display.
 * < 1e6   -> locale grouping with up to `decimals` fractional digits when small.
 * >= 1e6  -> suffix notation (1.23M) until the suffix table runs out.
 * beyond  -> scientific (1.23e21).
 */
export function format(value: number, decimals = 2): string {
  const n = safeNumber(value);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);

  if (abs < 1000) {
    // Small numbers: show fractional part only when it carries meaning.
    if (Number.isInteger(abs)) return sign + abs.toLocaleString('en-US');
    return sign + abs.toLocaleString('en-US', { maximumFractionDigits: abs < 10 ? decimals : 0 });
  }

  if (abs < 1e6) {
    return sign + Math.floor(abs).toLocaleString('en-US');
  }

  const tier = Math.floor(Math.log10(abs) / 3);
  if (tier < SUFFIXES.length) {
    const scaled = abs / Math.pow(1000, tier);
    return sign + scaled.toFixed(decimals) + SUFFIXES[tier];
  }

  // Out of suffixes: scientific notation.
  return sign + abs.toExponential(decimals);
}

/** Format a per-second rate. */
export function formatRate(value: number, decimals = 2): string {
  return format(value, decimals) + '/s';
}

/** Format an integer with locale grouping. */
export function formatInt(value: number): string {
  return Math.floor(safeNumber(value)).toLocaleString('en-US');
}

/**
 * Geometric cost of the n-th purchase (0-indexed `owned`):
 *   cost(n) = base * growth^owned
 */
export function costOf(base: number, growth: number, owned: number): number {
  return safeNumber(base * Math.pow(growth, owned));
}

/**
 * Closed-form total cost of buying `count` items starting from `owned`:
 *   base * growth^owned * (growth^count - 1) / (growth - 1)
 */
export function bulkCost(base: number, growth: number, owned: number, count: number): number {
  if (count <= 0) return 0;
  if (growth === 1) return safeNumber(base * count);
  const first = base * Math.pow(growth, owned);
  return safeNumber((first * (Math.pow(growth, count) - 1)) / (growth - 1));
}

/**
 * Maximum number of items affordable with `money`, given geometric pricing.
 *   floor( log( money*(g-1)/(base*g^owned) + 1 ) / log(g) )
 */
export function maxAffordable(money: number, base: number, growth: number, owned: number): number {
  if (money <= 0) return 0;
  const first = base * Math.pow(growth, owned);
  if (money < first) return 0;
  if (growth === 1) return Math.floor(money / base);
  const n = Math.log((money * (growth - 1)) / first + 1) / Math.log(growth);
  return Math.max(0, Math.floor(n + 1e-9));
}

/** Reduce a list of multiplicative factors into a single multiplier. */
export function product(factors: number[]): number {
  let m = 1;
  for (const f of factors) m *= safeNumber(f, 1);
  return safeNumber(m, 1);
}

/** Linear interpolation. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp(t, 0, 1);
}

/** Format a duration given in seconds as a compact human string. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(safeNumber(seconds)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
